import type { Match } from '@sfc/sdk';
import { availabilityLabel, collaborationLabel, matchedLabel } from '../vocabulary';
import type { MatchesState } from './useMatches';

/**
 * The people you and somebody else both said yes to, and the conversations
 * with them.
 *
 * One list, because a match is a thread: there is nothing else to do with one.
 * Both sides are here -- ideas of yours somebody liked, and ideas of theirs
 * you liked -- since from this point the two are the same thing.
 */
export function MatchesScreen({
  state,
  onOpen,
}: {
  state: MatchesState;
  onOpen: (matchId: string) => void;
}): React.JSX.Element {
  if (state.status === 'loading') {
    return (
      <p className="loading" data-testid="matches-loading">
        Looking…
      </p>
    );
  }

  if (state.status === 'unreachable') {
    return (
      <div className="placeholder">
        <h2>We cannot reach the server</h2>
        <p className="notice notice--problem" role="status">
          Your matches are safe. Try again in a moment.
        </p>
      </div>
    );
  }

  if (state.matches.length === 0) {
    return (
      <div className="placeholder" data-testid="matches-empty">
        <h2>No matches yet</h2>
        <p>
          A match is two yeses: you like an idea, and the person who posted it accepts. Both of you
          find out at once.
        </p>
      </div>
    );
  }

  return (
    <section className="column" aria-label="Your matches">
      <ul className="people">
        {state.matches.map((match) => (
          <li key={match.id}>
            <button
              className="person person--open"
              type="button"
              data-testid="match"
              onClick={() => onOpen(match.id)}
            >
              <span className="person__head">
                <span className="person__name">
                  {match.person.displayName}
                  {match.unread > 0 && (
                    <span className="tab__badge" data-testid="match-unread">
                      {match.unread}
                      <span className="sr-only"> unread</span>
                    </span>
                  )}
                </span>
                <span className="fine">
                  {match.role === 'holder'
                    ? `They liked "${match.ideaTitle}"`
                    : `You liked "${match.ideaTitle}"`}
                </span>
              </span>

              {match.person.headline && (
                <span className="person__headline">{match.person.headline}</span>
              )}

              <span className="person__facts">
                {match.person.availability && (
                  <span className="chip">{availabilityLabel(match.person.availability)}</span>
                )}
                {match.person.collaborationType && (
                  <span className="chip">{collaborationLabel(match.person.collaborationType)}</span>
                )}
              </span>

              <Latest match={match} />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The last thing said, or an invitation to say the first thing.
 *
 * Clamped to a line by the stylesheet rather than cut short here, so nobody is
 * shown half a word and the full text is still what a screen reader gets.
 */
function Latest({ match }: { match: Match }): React.JSX.Element {
  if (match.closed) {
    return (
      <span className="fine" data-testid="match-closed">
        Conversation closed
      </span>
    );
  }

  if (!match.lastMessage) {
    return <span className="fine">{matchedLabel(match.matchedAt)} · say something</span>;
  }

  return (
    <span className="person__latest" data-testid="match-latest">
      {match.lastMessageFromMe && <span className="person__latest-who">You: </span>}
      {match.lastMessage}
    </span>
  );
}
